/*Copyright (c) 2026 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2026 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2026 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2026 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import React, { useState } from 'react';
import {
  Radio,
  RadioGroup,
  FormControl,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import { Modal } from 'components/generals/Modal';
import Typography from 'components/generals/Typography';
import Box from 'components/generals/Box';
import TextField from 'components/generals/TextField';
import FormControlLabel from 'components/generals/FormControlLabel';
import Button from 'components/generals/Button';
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'store/slices/auth.slice';
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context/ThemeContext";

/**
 * Enum for feedback types available in the feedback form.
 */
enum FeedbackType {
  BUG_REPORT = 'bug_report',
  IMPROVEMENT = 'improvement',
}

/**
 * Interface for embedded image data.
 */
interface EmbeddedImage {
  id: string;
  base64: string;
  name: string;
}

/**
 * Interface for feedback form data.
 */
interface FeedbackData {
  type: FeedbackType;
  description: string;
  images: EmbeddedImage[];
}

/**
 * Initial state for feedback form.
 */
const initialFeedbackData: FeedbackData = {
  type: FeedbackType.BUG_REPORT,
  description: '',
  images: [],
};

/**
 * Floating feedback button component with modal form.
 * 
 * Features:
 * - Fixed floating button always visible on screen
 * - Opens modal form on click
 * - Two feedback options: Bug Report, Improvement Suggestion
 * - Text field for detailed description
 * - Submit and cancel actions
 * 
 * This component is rendered globally in App.tsx to allow users
 * to provide feedback from any page in the application.
 * 
 * @returns Floating feedback button with modal form
 */
const FeedbackFloatingButton: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [feedbackData, setFeedbackData] = useState<FeedbackData>(initialFeedbackData);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [,, sendRequest] = useHttp();
  const currentUser = useSelector(selectCurrentUser);
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  /**
   * Opens the feedback modal.
   */
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  /**
   * Closes the feedback modal and resets form data.
   */
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setFeedbackData(initialFeedbackData);
  };

  /**
   * Handles feedback type change.
   */
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFeedbackData({
      ...feedbackData,
      type: event.target.value as FeedbackType,
    });
  };

  /**
   * Handles description text change.
   */
  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFeedbackData({
      ...feedbackData,
      description: event.target.value,
    });
  };

  /**
   * Reference to the description textarea for cursor position tracking.
   */
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  /**
   * Handles paste event to capture images from clipboard (GitHub-style).
   * Inserts a placeholder marker at the cursor position in the text.
   */
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          processImageFile(file);
        }
      }
    }
  };

  /**
   * Processes an image file and adds it to the feedback data.
   * Used by both paste and drag-and-drop handlers.
   */
  const processImageFile = (file: File): void => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>): void => {
      const base64 = e.target?.result as string;
      const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newImage: EmbeddedImage = {
        id: imageId,
        base64,
        name: file.name || `image-${(feedbackData.images || []).length + 1}.png`,
      };

      // Insert image placeholder at cursor position
      const textarea = textareaRef.current;
      const imagePlaceholder = `[IMG:${imageId}]`;

      setFeedbackData((prev) => {
        let newDescription = prev.description;
        if (textarea) {
          const cursorPos = textarea.selectionStart || prev.description.length;
          newDescription =
            prev.description.slice(0, cursorPos) +
            imagePlaceholder +
            prev.description.slice(cursorPos);
        } else {
          newDescription = prev.description + imagePlaceholder;
        }

        return {
          ...prev,
          description: newDescription,
          images: [...(prev.images || []), newImage],
        };
      });
    };
    reader.readAsDataURL(file);
  };

  /**
   * Handles drag over event to allow dropping images.
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Handles drop event to capture images dragged from device.
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        processImageFile(file);
      }
    }
  };

  /**
   * Removes an embedded image by id and its placeholder from the description.
   */
  const handleRemoveImage = (imageId: string): void => {
    setFeedbackData((prev) => ({
      ...prev,
      description: prev.description.replaceAll(`[IMG:${imageId}]`, ''),
      images: (prev.images || []).filter((img) => img.id !== imageId),
    }));
  };

  /**
   * Handles form submission.
   * Sends images as separate array for inline embedding via CID in the email.
   */
  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      sendRequest(
        CoreAPI_URLs.NOTIFICATIONS_SEND_FEEDBACK_EMAIL,
        () => {
          handleCloseModal();
        },
        "POST",
        JSON.stringify({
          userName: currentUser?.username || '',
          message: feedbackData.description,
          feedbackTitle: feedbackData.type,
          images: feedbackData.images || []
        }), null, { successMessage: t('FeedbackFloatingButton.successMessage') }
      );
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Gets the title for each feedback type.
   */
  const getFeedbackTypeTitle = (type: FeedbackType): string => {
    const titles: Record<FeedbackType, string> = {
      [FeedbackType.BUG_REPORT]: t('FeedbackFloatingButton.bugReportTitle'),
      [FeedbackType.IMPROVEMENT]: t('FeedbackFloatingButton.improvementTitle'),
    };
    return titles[type];
  };

  /**
   * Gets the description for each feedback type.
   */
  const getFeedbackTypeDescription = (type: FeedbackType): string => {
    const descriptions: Record<FeedbackType, string> = {
      [FeedbackType.BUG_REPORT]: t('FeedbackFloatingButton.bugReportDescription'),
      [FeedbackType.IMPROVEMENT]: t('FeedbackFloatingButton.improvementDescription'),
    };
    return descriptions[type];
  };

  /**
   * Validates if the form can be submitted.
   */
  const isFormValid = (): boolean => {
    return feedbackData.description.trim().length > 0;
  };

  return (
    <>
      {/* Floating Button */}
      <Tooltip title={t('FeedbackFloatingButton.tooltipTitle')} placement="left">
        <Box
          className="feedback-floating__button"
          bgColor="info"
          shadow="sm"
          color="white"
          id="cy-feedback-button"
          onClick={handleOpenModal}
        >
          <Icon fontSize="small" color="inherit">
            feedback
          </Icon>
        </Box>
      </Tooltip>

      <Modal open={isModalOpen} onClose={handleCloseModal} maxWidth="500px">
        <Box className={`feedback-floating__modal feedback-floating__modal--${darkMode ? 'dark' : 'light'}`}>
          <Box className={`feedback-floating__header feedback-floating__header--${darkMode ? 'dark' : 'light'}`}>
            <Typography variant="h4">{t('FeedbackFloatingButton.title')}</Typography>
          </Box>
          <Box className="feedback-floating__content">
            <FormControl className="feedback-floating__form-control">
              <Typography variant="h5" className="feedback-floating__form-title">{t('FeedbackFloatingButton.feedbackType')}</Typography>
              <RadioGroup
                name="feedbackType"
                value={feedbackData.type}
                onChange={handleTypeChange}
                defaultValue={FeedbackType.BUG_REPORT}
              >
                <FormControlLabel
                  value={FeedbackType.BUG_REPORT}
                  control={<Radio />}
                  label={<Typography variant="body1">{getFeedbackTypeTitle(FeedbackType.BUG_REPORT)}</Typography>}
                />
                <Typography variant="caption" color="text" className="feedback-floating__radio-description">
                  {getFeedbackTypeDescription(FeedbackType.BUG_REPORT)}
                </Typography>
                <FormControlLabel
                  value={FeedbackType.IMPROVEMENT}
                  control={<Radio />}
                  label={<Typography variant="body1">{getFeedbackTypeTitle(FeedbackType.IMPROVEMENT)}</Typography>}
                />
                <Typography variant="caption" color="text" className="feedback-floating__radio-description">
                  {getFeedbackTypeDescription(FeedbackType.IMPROVEMENT)}
                </Typography>
              </RadioGroup>
            </FormControl>
            <Box onPaste={handlePaste} onDragOver={handleDragOver} onDrop={handleDrop}>
              <TextField
                inputRef={textareaRef}
                label={t('FeedbackFloatingButton.descriptionLabel')}
                multiline
                rows={4}
                fullWidth
                value={feedbackData.description}
                onChange={handleDescriptionChange}
                InputLabelProps={{ shrink: true }}
                placeholder={
                  feedbackData.type === FeedbackType.BUG_REPORT
                    ? t('FeedbackFloatingButton.bugPlaceholder')
                    : t('FeedbackFloatingButton.improvementPlaceholder')
                }
              />
              <Typography variant="caption" color="secondary" className="feedback-floating__paste-hint">
                <Icon className="feedback-floating__paste-icon">image</Icon>
                {t('FeedbackFloatingButton.pasteHint')}
              </Typography>
            </Box>
            {(feedbackData.images || []).length > 0 && (
              <Box className="feedback-floating__images-section">
                <Typography variant="subtitle2" className="feedback-floating__images-title">
                  {t('FeedbackFloatingButton.attachedImages')} ({(feedbackData.images || []).length})
                </Typography>
                <Box className="feedback-floating__images-container">
                  {(feedbackData.images || []).map((img) => (
                    <Box key={img.id} className="feedback-floating__image-wrapper">
                      <img
                        src={img.base64}
                        alt={img.name}
                        className="feedback-floating__image"
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(img.id)}
                        className="feedback-floating__image-remove"
                      >
                        <Icon className="feedback-floating__remove-icon">close</Icon>
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Box className={`feedback-floating__actions feedback-floating__actions--${darkMode ? 'dark' : 'light'}`}>
            <Button
              id="cy-feedback-cancel"
              color="secondary"
              variant="contained"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              {t('FeedbackFloatingButton.cancel')}
            </Button>
            <Button
              id="cy-feedback-submit"
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? t('FeedbackFloatingButton.submitting') : t('FeedbackFloatingButton.submit')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FeedbackFloatingButton;
